/**
 * ImpersonationBanner — sticky bar shown across the top of every page
 * whenever an admin is "logged in as" another user. Reads metadata from
 * localStorage (written by startImpersonation in utils/impersonation),
 * shows a live countdown to expiry, and offers a one-click "Return to
 * admin" that calls /auth/impersonate/end and restores the cached admin
 * token. If the 30-min token expires unattended, the banner self-ends
 * the session (skipApi=true) and routes back to the support console.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { endImpersonation, getImpersonationMeta } from '../utils/impersonation';

export default function ImpersonationBanner() {
  const { setUser } = useAuth();
  const navigate    = useNavigate();
  const [meta, setMeta] = useState(() => getImpersonationMeta());
  const [now, setNow]   = useState(Date.now());
  const [busy, setBusy] = useState(false);

  // Re-poll meta on mount + on storage events (other tabs).
  useEffect(() => {
    const refresh = () => setMeta(getImpersonationMeta());
    refresh();
    window.addEventListener('storage', refresh);
    return () => window.removeEventListener('storage', refresh);
  }, []);

  // 1-second ticker for the countdown (only when active).
  useEffect(() => {
    if (!meta) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [meta]);

  if (!meta) return null;

  const expMs = meta.expires_at ? new Date(meta.expires_at).getTime() : 0;
  const remaining = Math.max(0, Math.floor((expMs - now) / 1000));
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const urgent = remaining < 60;

  // Auto-end on expiry (token is dead, skip API call to avoid 401 loop).
  if (remaining === 0 && expMs > 0 && !busy) {
    setBusy(true);
    toast('Impersonation session expired', { icon: '⏱' });
    endImpersonation({ setUser, navigate, skipApi: true }).finally(() => {
      setMeta(null); setBusy(false);
    });
  }

  const end = async () => {
    setBusy(true);
    try {
      await endImpersonation({ setUser, navigate });
      toast.success('Returned to admin');
      setMeta(null);
    } catch (e) { toast.error('Could not end session'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{
      flexShrink: 0, width: '100%',
      background: urgent
        ? 'linear-gradient(90deg,#7f1d1d,#dc2626,#7f1d1d)'
        : 'linear-gradient(90deg,#7c2d12,#ea580c,#7c2d12)',
      backgroundSize: '200% 100%', animation: 'hm-imp-shimmer 6s linear infinite',
      color: '#fff', fontFamily: "'Inter',sans-serif",
      padding: '8px 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 4px 14px rgba(124,45,18,.45)',
      borderBottom: '1px solid rgba(255,255,255,.15)',
      flexWrap: 'wrap', boxSizing: 'border-box', zIndex: 200,
    }}>
      <style>{`
        @keyframes hm-imp-shimmer { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
        @keyframes hm-imp-pulse { 0%,100%{transform:scale(1);opacity:.85} 50%{transform:scale(1.18);opacity:1} }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1, fontSize: 12.5 }}>
        <span style={{
          display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
          background: '#fde68a', animation: 'hm-imp-pulse 1.5s ease-in-out infinite',
        }} />
        <span style={{ fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', fontSize: 11 }}>
          Acting as
        </span>
        <span style={{ fontWeight: 700 }}>{meta.target_name}</span>
        <span style={{ opacity: .8, fontSize: 11, padding: '1px 7px', background: 'rgba(255,255,255,.15)', borderRadius: 99, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {meta.target_role}
        </span>
        {meta.reason && (
          <span style={{ opacity: .85, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
            · {meta.reason}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{
          fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
          padding: '3px 9px', borderRadius: 6,
          background: urgent ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.22)',
          letterSpacing: '.06em',
        }} title="Session expires in">
          ⏱ {mm}:{ss}
        </span>
        <button onClick={end} disabled={busy}
          style={{
            background: '#fff', color: '#7c2d12',
            border: 'none', cursor: busy ? 'wait' : 'pointer',
            padding: '6px 14px', borderRadius: 7,
            fontFamily: "'Inter',sans-serif", fontSize: 12, fontWeight: 700,
            letterSpacing: '.04em', textTransform: 'uppercase',
            boxShadow: '0 2px 6px rgba(0,0,0,.2)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: busy ? 0.7 : 1,
          }}>
          ← Return to admin
        </button>
      </div>
    </div>
  );
}
